import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text, Caption } from "./typography";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { API_BASE_URL } from '../../lib/api';

/**
 * AddDividendDialog - Dialog for adding a new dividend
 *
 * Supports two statement types:
 * - INDIVIDUAL: Per-ticker dividend with full details
 * - ANNUAL: Annual broker statement with total dividend
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when dividend is created successfully
 */
export function AddDividendDialog({
  open,
  onOpenChange,
  onSuccess,
}) {
  const [formData, setFormData] = React.useState({
    statementType: "INDIVIDUAL",
    // Individual dividend fields
    assetName: "",
    ticker: "",
    dividendPerShare: "",
    sharesOwned: "",
    // Annual statement fields
    broker: "",
    totalAmount: "",
    // Common fields
    paymentDate: new Date().toISOString().split("T")[0],
    currency: "EUR",
    notes: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const CURRENCIES = [
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "SEK", label: "SEK - Swedish Krona" },
    { value: "NOK", label: "NOK - Norwegian Krone" },
  ];

  const isAnnual = formData.statementType === "ANNUAL";

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        statementType: "INDIVIDUAL",
        assetName: "",
        ticker: "",
        dividendPerShare: "",
        sharesOwned: "",
        broker: "",
        totalAmount: "",
        paymentDate: new Date().toISOString().split("T")[0],
        currency: "EUR",
        notes: "",
      });
      setError(null);
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (isAnnual) {
      if (!formData.broker.trim()) {
        return "Broker is required";
      }
      if (!formData.totalAmount || parseFloat(formData.totalAmount) < 0) {
        return "Total amount must be non-negative";
      }
      if (!formData.paymentDate) {
        return "Year end date is required";
      }
    } else {
      if (!formData.assetName.trim()) {
        return "Asset name is required";
      }
      if (!formData.ticker.trim()) {
        return "Ticker symbol is required";
      }
      if (!formData.dividendPerShare || parseFloat(formData.dividendPerShare) < 0) {
        return "Dividend per share must be non-negative";
      }
      if (!formData.sharesOwned || parseFloat(formData.sharesOwned) <= 0) {
        return "Shares owned must be positive";
      }
      if (!formData.paymentDate) {
        return "Payment date is required";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        statementType: formData.statementType,
        currency: formData.currency,
        paymentDate: formData.paymentDate,
        notes: formData.notes || "",
      };

      if (isAnnual) {
        payload.broker = formData.broker;
        payload.totalAmount = parseFloat(formData.totalAmount);
      } else {
        payload.assetName = formData.assetName;
        payload.ticker = formData.ticker;
        payload.dividendPerShare = parseFloat(formData.dividendPerShare);
        payload.sharesOwned = parseFloat(formData.sharesOwned);
      }

      const response = await axios.post(
        `${API_BASE_URL}/transactions/dividends`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess(response.data);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to create dividend:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle>Add Dividend</DialogTitle>
          <DialogDescription>
            Record a dividend payment from your investments
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Statement Type Selection */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Statement Type
            </legend>
            <RadioGroup
              value={formData.statementType}
              onValueChange={(value) => handleSelectChange("statementType", value)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="INDIVIDUAL" id="individual" />
                <Label htmlFor="individual" className="text-sm cursor-pointer">
                  Individual Dividend (per stock)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ANNUAL" id="annual" />
                <Label htmlFor="annual" className="text-sm cursor-pointer">
                  Annual Statement (total from broker)
                </Label>
              </div>
            </RadioGroup>
            <Caption className="block text-muted-foreground">
              {isAnnual
                ? "Use this for annual broker statements with aggregated dividends."
                : "Use this for recording individual dividend payments."}
            </Caption>
          </fieldset>

          {/* Individual Dividend Fields */}
          {!isAnnual && (
            <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
              <legend className="px-2 text-sm font-medium text-muted-foreground">
                Stock Details
              </legend>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Asset Name *</Label>
                  <Input
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleInputChange}
                    placeholder="e.g., Apple Inc."
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Ticker *</Label>
                  <Input
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleInputChange}
                    placeholder="e.g., AAPL"
                    required={!isAnnual}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Dividend per Share *</Label>
                  <Input
                    name="dividendPerShare"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={formData.dividendPerShare}
                    onChange={handleInputChange}
                    placeholder="e.g., 0.24"
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Shares Owned *</Label>
                  <Input
                    name="sharesOwned"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={formData.sharesOwned}
                    onChange={handleInputChange}
                    placeholder="e.g., 100"
                    required={!isAnnual}
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Annual Statement Fields */}
          {isAnnual && (
            <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
              <legend className="px-2 text-sm font-medium text-muted-foreground">
                Annual Statement Details
              </legend>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Broker *</Label>
                <Input
                  name="broker"
                  value={formData.broker}
                  onChange={handleInputChange}
                  placeholder="e.g., Interactive Brokers, Trading 212"
                  required={isAnnual}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Total Dividend Amount *</Label>
                <Input
                  name="totalAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.totalAmount}
                  onChange={handleInputChange}
                  placeholder="e.g., 1500.00"
                  required={isAnnual}
                />
              </div>
            </fieldset>
          )}

          {/* Common Fields */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Payment Details
            </legend>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">
                  {isAnnual ? "Year End Date *" : "Payment Date *"}
                </Label>
                <Input
                  name="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleSelectChange("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-foreground text-sm">Notes</Label>
              <Input
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Optional notes about this dividend..."
              />
            </div>
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Dividend"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddDividendDialog;
